import React, { useEffect, useState } from 'react';
import {
  Box, Grid, Card, Typography, CircularProgress, Link,
  Divider, Pagination, IconButton, Tooltip, Chip, Stack,
  Collapse, Button, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import LinkIcon from '@mui/icons-material/Link';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { getCompanyProjects, deleteProjectById } from '../../../helper/organizationsApis';
import EditProjectDialog from './EditProjectDialog';
import { downloadProjectPDF } from './generateProjectsPDF';

const ITEMS_PER_PAGE = 2;

const PendingProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [editingProject, setEditingProject] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [expandedProjects, setExpandedProjects] = useState([]);

  const statusColors = {
  Pending: '#ff9800',
  Approved: '#4caf50',
  Rejected: '#f44336',
  };

  useEffect(() => {
    const fetchProjects = async () => {
    try {
      const data = await getCompanyProjects();
      const pendingProjects = data.filter(p => p.status === 'Pending');
      setProjects(pendingProjects);
      } catch (error) {
        //console.error('Failed to fetch projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const handlePageChange = (_, value) => setPage(value);
  const toggleExpand = (id) => {
    setExpandedProjects((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };

  const handleDownloadPDF = (project) => downloadProjectPDF(project);

  const handleDeleteClick = (project) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteProject = async () => {
    if (!projectToDelete) return;
    try {
      await deleteProjectById(projectToDelete._id);
      setProjects(prev => prev.filter(p => p._id !== projectToDelete._id));
    } catch (err) {
      //console.error('Delete failed:', err);
    } finally {
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    }
  };

  const paginatedProjects = projects.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;
  if (!projects.length) {
  return (
    <Box textAlign="center" paddingTop={10} mt={6}>
      <Typography variant="h6" color="textSecondary">
        No projects available at the moment.
      </Typography>
      <Typography variant="body1" mt={2}>
        Try adding a new project or check back later.
      </Typography>
    </Box>
  );
}

  return (
      <Box
        sx={{
          inlineSize: '100%',
          backgroundColor: '#f4f6f8',
          padding: "80px 20px 20px 20px",
          blockSize: '100%',
          overflowY: 'auto',
          position: 'relative',
        }}
      >
      <Typography variant="h6" gutterBottom textAlign="start" fontWeight={400} marginBottom="20px">
                My Pending Projects
      </Typography>
      <Grid container spacing={4}>
        {paginatedProjects.map((project, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card
              sx={{
                width : 600,
                p: 3,
                borderRadius: 4,
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                backgroundColor: '#ffffff',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-6px)',
                  boxShadow: '0 16px 32px rgba(0,0,0,0.15)',
                },
              }}
            >
             <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={4} marginTop="-14px" marginBottom="10px">
               <Stack direction="row">
                <Chip 
                  label={project.status} 
                  sx={{ 
                    backgroundColor: statusColors[project.status] || 'grey',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.8rem',
                  }} 
                />
              </Stack>
               <Stack direction="row" spacing={1}>
                <Tooltip title="Download PDF">
                  <IconButton onClick={() => handleDownloadPDF(project)}><DownloadIcon color="primary" /></IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton onClick={() => setEditingProject(project)}><EditIcon color="primary" /></IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton onClick={() => handleDeleteClick(project)}><DeleteIcon color="error" /></IconButton>
                </Tooltip>
                </Stack>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center" gap={4}>
                <Typography variant="body" fontWeight={400} ><strong>Projects Title: </strong><span>{project.projectTitle}</span></Typography>
              </Stack>
              {/* Project Summary */}
              <Typography variant="subtitle1" mt={2} mb={2} color="text.secondary">
                {project.projectSummary}
              </Typography>
              {/* Company Info */}
              <Stack spacing={1.5} mb={3}>
                <Typography display="flex" alignItems="center" fontWeight={600}>
                  <WorkIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  {project.companyName}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {project.companyOverview}
                </Typography>

                {project.websiteOrLinkedIn && (
                  <Typography variant="body2" display="flex" alignItems="center">
                    <LinkIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                    <Link href={project.websiteOrLinkedIn} target="_blank" rel="noopener" underline="hover">
                      {project.websiteOrLinkedIn}
                    </Link>
                  </Typography>
                )}
              </Stack>

              {/* Tags: Domain, Tech Stack, Programming Languages */}
              <Stack direction="row" justifyContent="flex-start" alignItems="left" gap={4} flexWrap="wrap" margin="10px 20px 10px 0px ">
                {project.domain && (
                  <Chip
                    label={project.domain}
                    color="primary"
                    variant="outlined"
                    sx={{ margin: '2px', fontWeight: 500 }}
                  />
                )}
                {project.preferredTechStack && (
                  <Chip
                    label={project.preferredTechStack}
                    color="secondary"
                    variant="outlined"
                    sx={{ margin: '2px', fontWeight: 500, }}
                  />
                )}
                {project.preferredProgrammingLanguages && (
                  <Chip
                    label={project.preferredProgrammingLanguages}
                    variant="outlined"
                    sx={{ margin: '2px', fontWeight: 500  }}
                  />
                )}
              </Stack>
             <Collapse in={expandedProjects.includes(project._id)}>
                <Divider sx={{ mb: 2 }} />

                {/* Project Details */}
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Project Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>Deliverables:</strong> {project.expectedDeliverables}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>Duration:</strong> {project.projectDuration}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>Time/Week:</strong> {project.weeklyTimeCommitment}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>Mode:</strong> {project.modeOfWork}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>Compensation:</strong> {project.compensation}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2"><strong>Confidentiality:</strong> {project.confidentialityOrIPClauses}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" display="flex" alignItems="center">
                      <CalendarTodayIcon fontSize="small" sx={{ mr: 1 }} />
                      <strong>Start:</strong>&nbsp;{new Date(project.desiredStartDate).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" display="flex" alignItems="center">
                      <CalendarTodayIcon fontSize="small" sx={{ mr: 1 }} />
                      <strong>Deadline:</strong>&nbsp;{new Date(project.applicationDeadline).toLocaleDateString()}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Point of Contact */}
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Point of Contact
                </Typography>
                <Typography variant="body2">{project.pointOfContact?.name} – {project.pointOfContact?.designation}</Typography>
                <Typography variant="body2">Email: {project.pointOfContact?.email}</Typography>
                <Typography variant="body2">Phone: {project.pointOfContact?.phoneNumber}</Typography>

                {/* Attachment */}
                {project.s3Url && (
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    <strong>📎 Attachment:</strong> <Link href={project.s3Url} target="_blank" rel="noopener">Download</Link>
                  </Typography>
                )}
              </Collapse>
              <Button
                size="small"
                variant="outlined"
                sx={{ mt: 2, color: '#1976d2', textTransform: 'none' }}
                onClick={() => toggleExpand(project._id)}
              >
                {expandedProjects.includes(project._id) ? 'View Less' : 'View More'}
              </Button>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box mt={5} display="flex" justifyContent="center">
        <Pagination
          count={Math.ceil(projects.length / ITEMS_PER_PAGE)}
          page={page}
          onChange={handlePageChange}
          color="primary"
          shape="rounded"
        />
      </Box>

      <EditProjectDialog
        open={!!editingProject}
        onClose={() => setEditingProject(null)}
        project={editingProject}
        onUpdate={(updated) => {
          setProjects(prev =>
            prev.map((p) => (p._id === updated._id ? updated : p))
          );
        }}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent>
          Are you sure you want to delete <strong>{projectToDelete?.projectTitle}</strong>?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteProject} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingProjects;
